import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dumbbell,
  House,
  Trophy,
  Clock3,
  RotateCcw,
  CheckCircle2,
  Play
} from 'lucide-react';

function calcularDuracao(treino) {
  const exercicios = Array.isArray(treino?.exercicios)
    ? treino.exercicios
    : [];

  const rodadas = Math.max(
    1,
    Number(treino?.rodadas || 1)
  );

  const segundosRodada = exercicios.reduce(
    (total, exercicio) => {
      return total
        + Number(exercicio?.duracao || 0)
        + Number(exercicio?.descanso || 0);
    },
    0
  );

  return segundosRodada * rodadas;
}

function formatarDuracao(segundos) {
  const total = Math.max(0, Number(segundos || 0));

  if (total < 60) {
    return `${total}s`;
  }

  const minutos = Math.floor(total / 60);
  const resto = total % 60;

  if (!resto) {
    return `${minutos} min`;
  }

  return `${minutos} min ${resto}s`;
}

export default function AlunoTreinosCasaPage() {
  const [treinos, setTreinos] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.get('/aluno/treinos-casa');

      setTreinos(
        Array.isArray(res.data?.treinos)
          ? res.data.treinos
          : []
      );
    } catch {
      toast.error(
        'Erro ao carregar os treinos em casa'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <div
      className="space-y-6 animate-fadeIn"
      data-testid="aluno-treinos-casa-page"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            backgroundColor:
              'color-mix(in srgb, var(--cor-primaria, #007AFF) 15%, transparent)'
          }}
        >
          <House
            className="w-5 h-5"
            style={{
              color:
                'var(--cor-primaria, #007AFF)'
            }}
          />
        </div>

        <div>
          <h1 className="text-2xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
            MEUS TREINOS
          </h1>

          <p className="text-sm text-zinc-500">
            Treinos para realizar onde estiver
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 p-1 bg-[#0F0F0F] border border-[#27272A] rounded-xl">
        <Link
          to="/aluno/treinos"
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-zinc-400 hover:text-white hover:bg-[#181818] transition-colors"
        >
          <Dumbbell className="w-4 h-4" />
          Presencial
        </Link>

        <div
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{
            backgroundColor:
              'var(--cor-primaria, #007AFF)'
          }}
        >
          <House className="w-4 h-4" />
          Em Casa
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(item => (
            <Skeleton
              key={item}
              className="h-36 bg-[#141414]"
            />
          ))}
        </div>
      ) : treinos.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-10 text-center">
            <House className="w-10 h-10 text-zinc-600 mx-auto mb-3" />

            <p className="text-white font-semibold">
              Nenhum treino em casa disponível
            </p>

            <p className="text-sm text-zinc-500 mt-1">
              Quando seu professor disponibilizar um treino,
              ele aparecerá aqui.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm tracking-[0.15em] uppercase font-bold text-zinc-500">
              Treinos disponíveis
            </h2>

            <span className="text-xs text-zinc-600">
              {treinos.length} treino(s)
            </span>
          </div>

          {treinos.map(treino => {
            const exercicios = Array.isArray(
              treino.exercicios
            )
              ? treino.exercicios
              : [];

            const duracao = calcularDuracao(
              treino
            );

            return (
              <Card
                key={treino.id}
                className={`border transition-all ${
                  treino.concluido
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-[#141414] border-[#27272A] hover:border-[#3f3f46]'
                }`}
                data-testid={`treino-casa-card-${treino.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-white">
                          {treino.nome}
                        </h3>

                        {treino.concluido && (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Concluído
                          </Badge>
                        )}
                      </div>

                      {treino.descricao && (
                        <p className="text-sm text-zinc-500 mt-1">
                          {treino.descricao}
                        </p>
                      )}
                    </div>

                    <div className="shrink-0 flex items-center gap-1 text-yellow-400 font-bold">
                      <Trophy className="w-4 h-4" />
                      {Number(
                        treino.concluido
                          ? treino.xp_ganho
                          : treino.xp_conclusao
                      ) || 0}
                      <span className="text-xs">
                        XP
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <div className="bg-[#0F0F0F] rounded-lg p-3 text-center">
                      <Dumbbell className="w-4 h-4 text-zinc-500 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white">
                        {exercicios.length}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-zinc-600">
                        exercícios
                      </p>
                    </div>

                    <div className="bg-[#0F0F0F] rounded-lg p-3 text-center">
                      <RotateCcw className="w-4 h-4 text-zinc-500 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white">
                        {Number(treino.rodadas || 1)}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-zinc-600">
                        rodadas
                      </p>
                    </div>

                    <div className="bg-[#0F0F0F] rounded-lg p-3 text-center">
                      <Clock3 className="w-4 h-4 text-zinc-500 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white">
                        {formatarDuracao(duracao)}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-zinc-600">
                        estimado
                      </p>
                    </div>
                  </div>

                  <Button
                    asChild
                    className="w-full mt-4"
                    style={{
                      backgroundColor:
                        'var(--cor-primaria, #007AFF)'
                    }}
                  >
                    <Link
                      to={`/aluno/treinos-casa/${treino.id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {treino.concluido
                        ? 'Ver treino'
                        : 'Abrir treino'}
                    </Link>
                  </Button>

                  {exercicios.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#27272A]">
                      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 mb-2">
                        Exercícios
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {exercicios.map(exercicio => (
                          <Badge
                            key={`${treino.id}-${exercicio.ordem}`}
                            variant="outline"
                            className="border-[#303036] text-zinc-400"
                          >
                            {exercicio.ordem}. {exercicio.nome}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
